import React from "react";
import { StringDiff } from 'react-string-diff';
import {
    Accordion as MuiAccordion,
    AccordionSummary,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    withStyles,
    Switch,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { lista_estados } from "../../../../data/status_error_icons";
import icons from "../../../../theme/icons";
import iconsSemaforo from "../../../../theme/icons-semaforo.js";
import { Alert } from "@material-ui/lab";
import { corregirTextoAUTF8 } from "../../../../utils/strings.js";
import { useTranslation } from "react-i18next";

const Accordion = withStyles({
    root: {
        "&:not(:last-child)": {
            borderBottom: 0,
        },
        "&$expanded": {
            margin: "auto",
        },
    },
    expanded: {},
})(MuiAccordion);

export function StateIcon({ tooltipTitle, iconStyle }) {
    return (
        <Tooltip title={tooltipTitle} placement="left">
            <div
                style={{
                    height: "15px",
                    width: "15px",
                }}
            >
                <i
                    style={{
                        ...icons.sprite,
                        ...iconStyle,
                        zoom: "normal",
                        scale: "0.1",
                        transform: "none",
                    }}
                />
            </div>
        </Tooltip>
    );
}

export function NewPlusRegisters({ data, objective, showOnlyWithRegisters, esIVR }) {
    const [showDiff, setShowDiff] = React.useState(false);
    const { t } = useTranslation()
    const stepsSplit = React.useMemo(() => {
        if (!data) return null;
        const filteredNodes = showOnlyWithRegisters
            ? data.filter((nodo) => {
                  return (
                      nodo.fechasSteps.filter((fecha) => {
                          return (
                              fecha.steps.filter((step) => {
                                  return step.registros.length > 0;
                              }).length > 0
                          );
                      }).length > 0
                  );
              })
            : data;
        const processedData = filteredNodes.map((nodo) => {
            const newSteps = nodo.fechasSteps.map((date) => {
                const steps = date.steps.map((step) => {
                    const estados = step.estado?.split(",");
                    const patronToRegistro = []
                    const patrones = step.patrones
                        .sort(
                            (a, b) =>
                                parseInt(a["@orden"]) - parseInt(b["@orden"])
                        )
                        .map((patron, index) => {
                            if (esIVR) {
                                patron["@nombre"] = corregirTextoAUTF8(patron['transcription_patron']?.['#text'] ?? '')
                                patronToRegistro.push(patron["@nombre"])
                            }
                            return { ...patron, estado: estados[index] };
                        });
                    const registros = step.registros
                        .sort(
                            (a, b) =>
                                parseInt(a["@orden"]) - parseInt(b["@orden"])
                        )
                        .map((patron, index) => {
                            const text =
                                patron["#text"]
                                    ?.replace("<![CDATA[", "")
                                    .replace("]]>", "") ?? "";
                            return {
                                ...patron,
                                '@nombre': corregirTextoAUTF8(patron["@nombre"]),
                                estado: estados[index],
                                "#text": esIVR ? corregirTextoAUTF8(text) : text,
                                patronToRegistro: esIVR && patronToRegistro.length > 0 ? patronToRegistro[index] : ""
                            };
                        });
                    return { ...step, patrones, registros };
                });
                return {
                    fecha: date.fecha,
                    estado: date.estado,
                    newSteps: steps,
                };
            });
            return { nodo: nodo.dataNodo, dates: newSteps };
        });

        return processedData;
    }, [data, showOnlyWithRegisters]);

    if (!stepsSplit.length)
        return <Alert severity="info">{t('base-data-registers.alert.no-records')}</Alert>;

    return (
        <>
            {esIVR ?
                <Box display='flex' alignItems='center' justifyContent='end'>
                    <Typography variant='h6' style={{color: "#6b6b6b"}}>{t('new-plus-registers.switch.show-differences')}</Typography>
                    <Box style={{marginLeft: '10px'}}>
                        <Switch
                            checked={showDiff}
                            onChange={() => setShowDiff(!showDiff)}
                            inputProps={{ 'aria-label': 'secondary checkbox' }}
                            color='primary'
                        />
                    </Box>
                </Box>
            : null
            }
            
            {stepsSplit.map((nodo) => (
                <Accordion key={nodo.id_nodo}>
                    <AccordionSummary
                        className="at-blue-background"
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id={nodo.nodo.id_nodo}
                    >
                        <Typography style={{ color: "white" }}>
                            {nodo.nodo.nodo}
                        </Typography>
                    </AccordionSummary>
                    {nodo.dates.map((date) => (
                        <Accordion
                            style={{
                                backgroundColor: "#0f1d45",
                                borderRadius: 0,
                                margin: 0,
                            }}
                            key={date.fecha}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1a-content"
                                id={date.fecha}
                                key={date.fecha}
                            >
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    justifyContent="space-between"
                                    width="100%"
                                >
                                    <Typography style={{ color: "white" }}>
                                        {date.fecha}
                                    </Typography>
                                    {date.estado === "ok" ? (
                                        <Tooltip title="OK" placement="left">
                                            <TableCell
                                                style={{ borderBottom: "none" }}
                                            >
                                                <i
                                                    style={{
                                                        ...iconsSemaforo.sprite,
                                                        ...iconsSemaforo.spriteSemaforoverde,
                                                    }}
                                                />
                                            </TableCell>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Error" placement="left">
                                            <TableCell
                                                style={{ borderBottom: "none" }}
                                            >
                                                <i
                                                    style={{
                                                        ...iconsSemaforo.sprite,
                                                        ...iconsSemaforo.spriteSemafororojo,
                                                    }}
                                                />
                                            </TableCell>
                                        </Tooltip>
                                    )}
                                </Box>
                            </AccordionSummary>
                            {date.newSteps.map((step) => (
                                <Accordion
                                    key={step.paso_orden}
                                    style={{ borderRadius: 0 }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        style={{ backgroundColor: "#142452" }}
                                    >
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                        >
                                            <Typography
                                                style={{
                                                    color: "white",
                                                    marginLeft: 15,
                                                    fontSize: "14px",
                                                }}
                                            >
                                                {step.nombre}
                                            </Typography>
                                            {step.ip &&
                                            step.ip !== "" &&
                                            step.ip !== "-1" ? (
                                                <Typography
                                                    variant="caption"
                                                    style={{
                                                        color: "white",
                                                        marginLeft: 15,
                                                    }}
                                                >
                                                    {step.ip}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    </AccordionSummary>
                                    <Accordion>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            style={{
                                                backgroundColor: "#263975",
                                            }}
                                        >
                                            <Typography
                                                style={{
                                                    color: "white",
                                                    marginLeft: 30,
                                                }}
                                            >
                                                {t('new-plus-registers.accordion.summary.patterns')}
                                            </Typography>
                                        </AccordionSummary>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow
                                                    style={{
                                                        backgroundColor:
                                                            "#263975",
                                                        color: "white",
                                                    }}
                                                >
                                                    <TableCell
                                                        style={{
                                                            color: "white",
                                                        }}
                                                    >
                                                        {t('new-plus-registers.table.patterns.header.order')}
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            color: "white",
                                                        }}
                                                    >
                                                        {t('new-plus-registers.table.patterns.header.pattern')}
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            color: "white",
                                                        }}
                                                    >
                                                        {esIVR ?
                                                            t('new-plus-registers.table.patterns.header.expected-accuracy') :
                                                            t('new-plus-registers.table.patterns.header.inverse-mandatory')
                                                        }
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            color: "white",
                                                        }}
                                                    >
                                                        {t('new-plus-registers.table.patterns.header.status')}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {step.patrones.map((patron) => (
                                                    <TableRow
                                                        key={patron["@orden"]}
                                                    >
                                                        <TableCell>
                                                            {patron["@orden"]}
                                                        </TableCell>
                                                        <TableCell>
                                                            {patron["@nombre"]}
                                                        </TableCell>
                                                        <TableCell
                                                            style={{
                                                                backgroundColor:
                                                                    "white",
                                                                textAlign:
                                                                    "center",
                                                            }}
                                                        >
                                                            {!esIVR && (
                                                                <React.Fragment>
                                                                    {patron[
                                                                        "@es_inverso"
                                                                    ] === "0"
                                                                        ? `${t('text.no')} / `
                                                                        : `${t('text.yes')} / `}
                                                                    {patron[
                                                                        "@es_opcional"
                                                                    ] === "0"
                                                                        ? `${t('text.yes')}`
                                                                        : `${t('text.no')}`}
                                                                </React.Fragment>
                                                            )}
                                                            {esIVR && `${patron['transcription_patron_precision']} / ${patron['transcription_precision']}`}
                                                        </TableCell>
                                                        <TableCell>
                                                            {patron[
                                                                "estado"
                                                            ] === "0" ? (
                                                                <StateIcon
                                                                    tooltipTitle="Ok"
                                                                    iconStyle={
                                                                        icons.spriteOK
                                                                    }
                                                                    secondIconStyle={
                                                                        icons.spriteOK
                                                                    }
                                                                />
                                                            ) : patron[
                                                                  "estado"
                                                              ] in
                                                              lista_estados ? (
                                                                <StateIcon
                                                                    tooltipTitle={
                                                                        lista_estados[
                                                                            patron[
                                                                                "estado"
                                                                            ]
                                                                        ].nombre
                                                                    }
                                                                    iconStyle={
                                                                        icons[
                                                                            lista_estados[
                                                                                patron[
                                                                                    "estado"
                                                                                ]
                                                                            ]
                                                                                .icono
                                                                        ]
                                                                    }
                                                                />
                                                            ) : (
                                                                <StateIcon
                                                                    tooltipTitle="Periodo sin monitoreo"
                                                                    iconStyle={
                                                                        icons.spriteNO_MONITOREO
                                                                    }
                                                                />
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Accordion>
                                    <Accordion>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            style={{
                                                backgroundColor: "#263975",
                                            }}
                                        >
                                            <Typography
                                                style={{
                                                    color: "white",
                                                    marginLeft: 30,
                                                }}
                                            >
                                                {t('new-plus-registers.accordion.summary.registers')}
                                            </Typography>
                                        </AccordionSummary>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow
                                                    style={{
                                                        backgroundColor:
                                                            "#263975",
                                                        color: "white",
                                                    }}
                                                >
                                                    <TableCell
                                                        style={{
                                                            color: "white",
                                                        }}
                                                    >
                                                        {t('new-plus-registers.table.registers.header.order')}
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            color: "white",
                                                        }}
                                                    >
                                                        {t('new-plus-registers.table.registers.header.name')}
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            color: "white",
                                                        }}
                                                    >
                                                        {t('new-plus-registers.table.registers.header.value')}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {step.registros.map(
                                                    (registro) => (
                                                        <TableRow
                                                            key={
                                                                registro[
                                                                    "@orden"
                                                                ]
                                                            }
                                                        >
                                                            <TableCell>
                                                                {
                                                                    registro[
                                                                        "@orden"
                                                                    ]
                                                                }
                                                            </TableCell>
                                                            <TableCell>
                                                                {
                                                                    registro[
                                                                        "@nombre"
                                                                    ]
                                                                }
                                                            </TableCell>
                                                            <TableCell
                                                                style={{
                                                                    backgroundColor:
                                                                        "white",
                                                                    textAlign:
                                                                        "center",
                                                                }}
                                                            >
                                                                {showDiff ?
                                                                    <StringDiff oldValue={registro["patronToRegistro"]} newValue={registro["#text"]} />
                                                                    
                                                                : registro["#text"]
                                                                }
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                )}
                                            </TableBody>
                                        </Table>
                                    </Accordion>
                                </Accordion>
                            ))}
                        </Accordion>
                    ))}
                </Accordion>
            ))}
        </>
    );
}
